import { useState, type ReactNode } from 'react';
import {
  useNotification,
  useTranslate,
  useUpdate,
  type HttpError,
} from '@refinedev/core';
import {
  Alert,
  Button,
  Flex,
  Modal,
  Segmented,
  Spin,
  Typography,
  theme,
} from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import {
  ModStatus,
  type PublicationDetailOut,
  type PublicationTree,
  type PublicationUpdateIn,
  type TreeNode,
} from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { usePublicationTrees } from '@/hooks/user_catalog/usePublicationTrees';
import { usePublisherStanding } from '@/hooks/tenancy/usePublisherStanding';
import { extractBackendMessage, type ApiHttpError } from '@/utils/apiError';
import {
  buildModStatusPatch,
  changedTargets,
  currentStatuses,
  refusalsByTarget,
  type ModStatusDraft,
  type ModStatusRefusals,
  type ModStatusTarget,
} from './publicationModStatusEdit';

const { Text } = Typography;

interface PublicationModStatusEditModalProps {
  publicationId: number;
  /** Shown in the title until the publication has loaded. */
  title?: string | null;
  open: boolean;
  onClose: () => void;
  /** The change was saved. */
  onSaved?: () => void;
}

/** Whether a dependent meets what enabling the publication needs. */
const Met = ({ met }: { met: boolean }) => {
  const { token } = theme.useToken();
  return met ? (
    <CheckCircleFilled style={{ color: token.colorSuccess }} />
  ) : (
    <CloseCircleFilled style={{ color: token.colorError }} />
  );
};

/**
 * Moderation of one publication: its own status and each format's, picked
 * separately and saved in one request — the backend applies it whole or
 * refuses it whole, and its reasons are shown on the row they concern.
 *
 * Below, what enabling the publication depends on: publisher (and, when it
 * is not enabled, why it could not be), authors, and lectors for context.
 * Information only — their status is changed on their own pages, and the
 * modal predicts nothing: the backend's answer is the verdict.
 */
export const PublicationModStatusEditModal = ({
  publicationId,
  title,
  open,
  onClose,
  onSaved,
}: PublicationModStatusEditModalProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { open: notify } = useNotification();
  const k = (key: string, opts?: Record<string, unknown>) =>
    t(`user_catalog.publications.mod_status_modal.${key}`, opts);
  const statusLabel = (status: ModStatus) => t(`base.mod_status.${status}`);

  const { trees, isLoading, isError, refetch } = usePublicationTrees(
    [publicationId],
    [],
    open
  );
  const tree: PublicationTree | undefined = trees?.trees?.[0];

  // Only the user's picks are stored; the rest reads the loaded statuses,
  // so a refetch never overwrites a pick.
  const [draft, setDraft] = useState<ModStatusDraft>({});
  const [refusals, setRefusals] = useState<ModStatusRefusals | null>(null);

  const { mutate, mutation } = useUpdate<
    PublicationDetailOut,
    HttpError,
    PublicationUpdateIn
  >();
  const saving = mutation.isPending;

  const publisher = tree?.publisher ?? null;
  const publisherEnabled = publisher?.mod_status === ModStatus.enabled;
  const standing = usePublisherStanding(publisher?.id, {
    enabled: open && publisher != null && !publisherEnabled,
  });

  const current = tree ? currentStatuses(tree) : {};
  const changed = tree ? changedTargets(tree, draft) : [];
  const targetLabel = (target: ModStatusTarget) =>
    target === 'publication'
      ? k('publication')
      : t(`user_catalog.common.formats.${target}`);

  const save = () => {
    if (!tree) return;
    const values = buildModStatusPatch(tree, draft);
    if (!values) return;
    setRefusals(null);
    mutate(
      {
        resource: 'user_catalog.publications',
        id: publicationId,
        values,
        meta: { method: 'patch' },
        mutationMode: 'pessimistic',
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          notify?.({
            type: 'success',
            message: t('notifications.success'),
            description: k('success'),
          });
          onSaved?.();
          onClose();
        },
        onError: (error) => {
          const items = (error as ApiHttpError).detailItems ?? [];
          if (items.length === 0) {
            notify?.({
              type: 'error',
              message: t('notifications.error'),
              description: extractBackendMessage(error) ?? error.message,
            });
            return;
          }
          // Refused as a whole: nothing was saved, the picks stay.
          setRefusals(
            refusalsByTarget(
              items,
              (id) => tree.authors?.find((a) => a.id === id)?.name
            )
          );
        },
      }
    );
  };

  const section = (label: string, children: ReactNode, extra?: ReactNode) => (
    <div>
      <Flex justify="space-between" align="baseline" gap={token.marginSM}>
        <Text
          type="secondary"
          strong
          style={{
            fontSize: token.fontSizeSM,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Text>
        {extra}
      </Flex>
      <Flex vertical gap={token.marginXS} style={{ marginTop: token.marginXS }}>
        {children}
      </Flex>
    </div>
  );

  const targetRow = (target: ModStatusTarget) => {
    const now = current[target];
    if (now == null) return null;
    const picked = draft[target] ?? now;
    const isChanged = changed.includes(target);
    const reasons = refusals?.byTarget[target] ?? [];
    return (
      <div
        key={target}
        style={{
          border: `${token.lineWidth}px solid ${
            reasons.length > 0
              ? token.colorErrorBorder
              : isChanged
                ? token.colorPrimaryBorder
                : token.colorBorderSecondary
          }`,
          background: isChanged ? token.colorPrimaryBg : token.colorBgContainer,
          borderRadius: token.borderRadiusLG,
          padding: `${token.paddingXS}px ${token.paddingSM}px`,
        }}
      >
        <Flex align="center" gap={token.marginSM} wrap>
          <Text strong style={{ minWidth: 96 }}>
            {targetLabel(target)}
          </Text>
          <ModStatusTag value={now} />
          <Segmented
            size="small"
            style={{ marginLeft: 'auto' }}
            value={picked}
            disabled={saving}
            options={Object.values(ModStatus).map((value) => ({
              value,
              label: statusLabel(value),
            }))}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, [target]: value as ModStatus }))
            }
          />
        </Flex>
        {reasons.length > 0 && (
          <ul
            style={{
              margin: `${token.marginXS}px 0 0`,
              paddingInlineStart: token.paddingLG,
              color: token.colorError,
            }}
          >
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const dependentRow = (
    resource: string,
    node: TreeNode,
    requiredEnabled: boolean
  ) => (
    <Flex key={node.id} align="center" gap={token.marginXS} wrap>
      {requiredEnabled && <Met met={node.mod_status === ModStatus.enabled} />}
      <ShowIconLink resource={resource} id={node.id}>
        {node.name}
      </ShowIconLink>
      <ModStatusTag value={node.mod_status} />
    </Flex>
  );

  const required = k('required', { status: statusLabel(ModStatus.enabled) });
  const authors = tree?.authors ?? [];
  const lectors = tree?.lectors ?? [];

  const body = () => {
    if (isLoading) return <Spin />;
    if (isError || !tree)
      return (
        <Alert
          type="error"
          showIcon
          message={k('load_error')}
          action={
            <Button size="small" onClick={() => refetch()}>
              {t('actions.retry')}
            </Button>
          }
        />
      );
    const publisherViolations =
      !publisherEnabled && !standing.isLoading && !standing.isError
        ? standing.standing.violations
        : [];
    return (
      <Flex vertical gap={token.marginMD}>
        {refusals && (
          <Alert
            type="error"
            showIcon
            message={k('refused')}
            description={
              refusals.general.length > 0 && (
                <ul style={{ margin: 0, paddingInlineStart: token.paddingLG }}>
                  {refusals.general.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )
            }
          />
        )}
        {section(
          k('section_targets'),
          (['publication', 'ebook', 'audio'] as ModStatusTarget[]).map(
            targetRow
          )
        )}
        {section(
          k('section_required'),
          <>
            <div>
              <Text type="secondary">
                {k('publisher')} · {required}
              </Text>
              {publisher ? (
                dependentRow('user_catalog.publishers', publisher, true)
              ) : (
                <Flex align="center" gap={token.marginXS}>
                  <Met met={false} />
                  <Text type="danger">{k('no_publisher')}</Text>
                </Flex>
              )}
              {publisherViolations.map((violation) => (
                <Text
                  key={violation}
                  type="danger"
                  style={{ display: 'block', fontSize: token.fontSizeSM }}
                >
                  {t(
                    `user_catalog.publishers.standing.violations.${violation}`
                  )}
                </Text>
              ))}
            </div>
            <div>
              <Text type="secondary">
                {k('authors')} ·{' '}
                {k('required_authors', {
                  status: statusLabel(ModStatus.enabled),
                })}
              </Text>
              {authors.length > 0 ? (
                authors.map((a) =>
                  dependentRow('user_catalog.authors', a, true)
                )
              ) : (
                <Flex align="center" gap={token.marginXS}>
                  <Met met={false} />
                  <Text type="danger">{k('no_authors')}</Text>
                </Flex>
              )}
            </div>
            <div>
              <Text type="secondary">
                {k('lectors')} · {k('lectors_info')}
              </Text>
              {lectors.length > 0 ? (
                lectors.map((l) =>
                  dependentRow('user_catalog.lectors', l, false)
                )
              ) : (
                <Text type="secondary" style={{ display: 'block' }}>
                  {k('none')}
                </Text>
              )}
            </div>
          </>,
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {k('dependents_hint')}
          </Text>
        )}
      </Flex>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={640}
      maskClosable={!saving}
      closable={!saving}
      title={k('title', {
        name: tree?.publication.name ?? title ?? `#${publicationId}`,
      })}
      footer={
        <Flex align="center" gap={token.marginSM}>
          <Text
            type="secondary"
            style={{ flex: 1, minWidth: 0, textAlign: 'left' }}
          >
            {changed.length > 0
              ? `${k('changes')} ${changed
                  .map(
                    (target) =>
                      `${targetLabel(target)} → ${statusLabel(draft[target] as ModStatus)}`
                  )
                  .join(' · ')}`
              : k('no_changes')}
          </Text>
          <Button onClick={onClose} disabled={saving}>
            {t('actions.cancel')}
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={changed.length === 0 || !tree}
            onClick={save}
          >
            {k('save')}
          </Button>
        </Flex>
      }
    >
      {body()}
    </Modal>
  );
};
